import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  List,
  Typography,
  Space,
  Divider,
  Card,
  Tooltip,
  ConfigProvider,
  Skeleton,
} from "antd";
import {
  BellOutlined,
  EllipsisOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import BackgroundNotification from "src/common/utils/notification";
import { Notification } from "src/common/types";
import coreFunctions from "src/common/utils/coreFunctions";
import InfiniteScroll from "react-infinite-scroll-component";

const { Text, Link } = Typography;

const NotificationDropdown: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState<Notification[]>([]);
  const [page, setPage] = useState(1);

  const NOTIFICATION = new BackgroundNotification();

  const getNotifications = async () => {
    if (localStorage.getItem("token")) {
      setChecking(true);

      setTimeout(async () => {
        const realData = await NOTIFICATION.getNotifications();
        setData(realData as Notification[]);
        setPage(page + 1);
      }, 1000);

      setTimeout(() => {
        setChecking(false);
      }, 1000);
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const dropdownContent = (
    <Card
      title={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text strong style={{ fontSize: 16 }}>
            Notifications
          </Text>
          <Link
            style={{ fontSize: 12, cursor: "pointer" }}
            // onClick={markAllAsRead}
          >
            Mark all as read
          </Link>
        </Space>
      }
      variant="outlined"
      style={{
        width: 380,
        maxHeight: 480,
        overflowY: "auto",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      }}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={getNotifications}
        hasMore={data.length < 50}
        loader={
          checking && (
            <Skeleton avatar paragraph={{ rows: 1 }} active={checking} />
          )
        }
        endMessage={<Divider plain>It is all, nothing more 🤐</Divider>}
        scrollableTarget="scrollableDiv"
      >
        <List
          dataSource={data}
          renderItem={(item) => {
            const isRead = item.status == 1;
            return (
              <List.Item
                style={{
                  backgroundColor: isRead ? "transparent" : "#EFF2F6",
                  transition: "background 0.2s",
                  padding: 16,
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#E3E6ED")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = isRead
                    ? "transparent"
                    : "#EFF2F6")
                }
                actions={[
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "mark-unread",
                          label: "Mark as unread",
                          onClick: () => NOTIFICATION.markAsRead(item.id),
                        },
                      ],
                    }}
                  >
                    <EllipsisOutlined
                      key="ellipsis"
                      style={{ fontSize: 16, cursor: "pointer" }}
                    />
                  </Dropdown>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <ConfigProvider
                      theme={{ components: { Badge: { dotSize: 8 } } }}
                    >
                      <Badge
                        dot
                        status={item.status === 0 ? "success" : "default"}
                        offset={[-5, 25]}
                      >
                        <Avatar style={{ backgroundColor: "#1890ff" }}>
                          {item.title[0]}
                        </Avatar>
                      </Badge>
                    </ConfigProvider>
                  }
                  title={
                    <Space direction="vertical" size={0}>
                      <Text strong={!isRead}>{item.title}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.message}
                      </Text>
                    </Space>
                  }
                  description={
                    <Space size="small" align="center">
                      <ClockCircleOutlined style={{ fontSize: 12 }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(item.date).toLocaleString()}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </InfiniteScroll>
      <Divider
        style={{ marginTop: "5px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)" }}
      />
      <div style={{ textAlign: "center" }}>
        <Link
          onClick={() => navigate("/notification-history")}
          style={{ color: "#1677ff", cursor: "pointer", fontSize: 12 }}
          strong
        >
          Notification history
        </Link>
      </div>
    </Card>
  );

  return (
    <Tooltip title="Notifications" placement="bottomRight">
      <Dropdown
        overlay={dropdownContent}
        trigger={["click"]}
        open={open}
        onOpenChange={(flag) => {
          if (coreFunctions.isMobileDevice()) {
            return navigate("/notification-history");
          } else {
            setOpen(flag);
          }
        }}
        placement="bottomRight"
      >
        <Badge count={data?.length}>
          <Button shape="circle" icon={<BellOutlined />} />
        </Badge>
      </Dropdown>
    </Tooltip>
  );
};

export default NotificationDropdown;
